'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { SocialLinks } from '@/components/contact/ContactComponents';
import { CONTACT_SETTINGS_FALLBACK } from '@/lib/contact-settings/fallback';
import type { PublicContactSettings } from '@/lib/contact-settings/schema';
import { isEnabledClient, type FlagKey } from '@/lib/feature-flags';

/** Footer links that can be rolled back without a code revert. */
const FLAGGED_LINKS: Record<string, FlagKey> = {
  '/engineering': 'engineeringPage',
  '/changelog': 'changelogPage',
};

const FOOTER_COLS = [
  {
    heading: 'Product',
    links: [
      { label: 'Features', href: '/features' },
      { label: 'Solutions', href: '/solutions' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Security', href: '/security' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'Legal', href: '/legal' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/legal/terms' },
      { label: 'Cookie Policy', href: '/cookies' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Help & Support', href: '/contact' },
      { label: 'Book a Demo', href: '/demo' },
      { label: 'Sign Up', href: '/signup' },
      { label: 'Customer Portal', href: '/customer-portal' },
      { label: 'Engineering', href: '/engineering' },
      { label: 'Changelog', href: '/changelog' },
    ],
  },
];

export default function MarketingFooter() {
  const [settings, setSettings] = useState<PublicContactSettings>(CONTACT_SETTINGS_FALLBACK);
  const [year, setYear] = useState(2026);

  useEffect(() => {
    setYear(new Date().getFullYear());
    // Lazy-import the Supabase browser client so the @supabase/ssr module is
    // never loaded during static prerender (where process.env is absent).
    import('@/lib/supabase/client').then(({ createClient }) => {
      const supabase = createClient();
      supabase
        .from('contact_settings')
        .select('company_name, social_links')
        .eq('id', 'singleton')
        .single()
        .then(({ data }) => {
          if (data) {
            setSettings((prev) => ({
              ...prev,
              companyName:
                ((data as Record<string, unknown>).company_name as string) || prev.companyName,
              socialLinks:
                ((data as Record<string, unknown>)
                  .social_links as PublicContactSettings['socialLinks']) || prev.socialLinks,
            }));
          }
        });
    });
  }, []);

  return (
    <footer className="border-t border-white/5 bg-[#080c18]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
          {/* Brand col */}
          <div className="col-span-2">
            <Link href="/" className="flex items-center gap-2 mb-4">
              <span className="text-sm font-bold text-white tracking-tight">
                {settings.companyName.toUpperCase()}
              </span>
            </Link>
            <p className="text-xs text-[#9ca3af] leading-relaxed max-w-xs mb-5">
              The diamond merchant management platform built for Thailand and the global gem trade.
            </p>
            {/* Newsletter */}
            <form onSubmit={(e) => e?.preventDefault()} className="flex gap-2">
              <input
                type="email"
                aria-label="Newsletter email address"
                placeholder="your@email.com"
                className="flex-1 px-3 py-2 rounded-lg bg-[#111827] border border-white/10 text-xs text-white placeholder-[#8b93a3] focus:outline-none focus:border-violet-500/50"
              />
              <button
                type="submit"
                className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium transition-colors"
              >
                Subscribe
              </button>
            </form>
          </div>

          {/* Link cols */}
          {FOOTER_COLS?.map((col) => (
            <div key={col?.heading}>
              <h2 className="text-xs font-semibold text-white uppercase tracking-wider mb-4">
                {col?.heading}
              </h2>
              <ul className="space-y-2.5">
                {col?.links
                  // Flagged links can be withdrawn by flipping an env var —
                  // the route stays reachable, the link disappears.
                  ?.filter((link) => {
                    const flag = FLAGGED_LINKS[link?.href ?? ''];
                    return flag ? isEnabledClient(flag) : true;
                  })
                  .map((link) => (
                    <li key={link?.href}>
                      <Link
                        href={link?.href}
                        className="text-xs text-[#9ca3af] hover:text-white transition-colors"
                      >
                        {link?.label}
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#8b93a3]">
            © {year} {settings.companyName}. All rights reserved.
          </p>
          <SocialLinks socialLinks={settings.socialLinks} />
        </div>
      </div>
    </footer>
  );
}
